"use client";

import { useMemo } from "react";
import { usePaginatedQuery, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  LISTING_BROWSE_PAGE_SIZE as PAGE_SIZE,
  type UseListingBrowseProductsArgs,
} from "./listing-browse-query";
import {
  useListingBrowseQueryArgs,
  useSmartFallbackArgs,
} from "./use-listing-browse-query-args";
import { useSearchResultPagination } from "./use-search-result-pagination";
import { useMarketplacePagination, useScopeTotal } from "./use-marketplace-pagination";

export function usePrimaryListingBrowseData(
  args: UseListingBrowseProductsArgs,
  page: number,
  setPage: (page: number) => void,
) {
  const queryArgs = useListingBrowseQueryArgs(args, page);
  const nearbyResult = useQuery(
    api.products.nearbyQueries.getNearbyWithFreightPaginated,
    queryArgs.nearbyArgs,
  );
  const searchPage = usePaginatedQuery(
    api.products.marketplaceSearch.searchMarketplacePage,
    queryArgs.searchArgs,
    { initialNumItems: PAGE_SIZE },
  );
  const searchResults = useMemo(
    () =>
      searchPage.results.flatMap((row) => (row.product ? [row.product] : [])),
    [searchPage.results],
  );
  const requiredSearchResults = useSearchResultPagination({
    hasSearch: queryArgs.hasSearch,
    status: searchPage.status,
    resultCount: searchResults.length,
    page,
    pageSize: PAGE_SIZE,
    setPage,
    loadMore: searchPage.loadMore,
  });
  const fallbackResult = useMarketplacePagination(
    queryArgs.fallbackArgs,
    queryArgs.listedAfter,
    setPage,
  );
  const scopeTotal = useScopeTotal(queryArgs.fallbackArgs, queryArgs.listedAfter);

  return {
    queryArgs,
    nearbyResult,
    searchRows: searchPage.results,
    searchStatus: searchPage.status,
    searchResults,
    requiredSearchResults,
    fallbackResult,
    scopeTotal: scopeTotal ?? null,
  };
}

export function useSmartFallbackData(
  args: UseListingBrowseProductsArgs,
  page: number,
  primary: ReturnType<typeof usePrimaryListingBrowseData>,
) {
  const shouldUseSmartFallback =
    !primary.queryArgs.hasSearch &&
    primary.queryArgs.hasLocation &&
    page === 1 &&
    primary.nearbyResult !== undefined &&
    primary.nearbyResult.products.length === 0;
  const smartFallbackArgs = useSmartFallbackArgs(
    args,
    primary.queryArgs,
    shouldUseSmartFallback,
  );
  const smartFallbackResult = useQuery(
    api.products.nearbyQueries.getSmartNearbyWithFreight,
    smartFallbackArgs,
  );

  return { shouldUseSmartFallback, smartFallbackResult };
}
